import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Milestone, CheckCircle2, Circle, Hourglass } from 'lucide-react';
import MilestoneDetailDialog from './MilestoneDetailDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function parseDate(iso) {
  if (!iso) return null;
  const d = new Date(iso + 'T00:00:00Z');
  return isNaN(d) ? null : d;
}
function fmt(date) {
  if (!date) return '';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

function phaseState(ph, now) {
  const start = parseDate(ph.start_date);
  const end = parseDate(ph.end_date);
  const total = ph.events_total || 0;
  const done = ph.events_done || 0;
  if (total > 0 && done === total) return 'done';
  if (end && end < now) return 'done';
  if (start && start > now) return 'upcoming';
  return 'active';
}

export default function RoadmapSection() {
  const [roadmaps, setRoadmaps] = useState([]);
  const [events, setEvents] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    axios.get(`${API}/planning/roadmaps`).then(({ data }) => setRoadmaps(data)).catch(() => {});
    // Public events feed lets the detail dialog show the "Linked tasks"
    // section for the clicked phase. Missing/non-public events are an
    // acceptable degradation — the dialog still renders the description.
    axios.get(`${API}/planning/events-public`).then(({ data }) => setEvents(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);

  // Flatten all phases across roadmaps, sorted by start_date
  const phases = useMemo(() => {
    const out = [];
    roadmaps.forEach((rm) => (rm.phases || []).forEach((ph) => out.push({ ...ph, roadmap: rm })));
    out.sort((a, b) => (a.start_date || '').localeCompare(b.start_date || ''));
    return out;
  }, [roadmaps]);

  if (phases.length === 0) return null;

  const now = new Date();
  const activeCount = phases.filter((p) => phaseState(p, now) === 'active').length;
  const doneCount = phases.filter((p) => phaseState(p, now) === 'done').length;

  // Build the props for the detail dialog from a phase row, enriching
  // it with the related events (filtered by phase_id) so the dialog
  // can render the "Linked tasks" section.
  const buildMilestone = (ph) => {
    const state = phaseState(ph, now);
    const accent = ph.color || (state === 'done' ? '#22C55E' : state === 'active' ? '#EAB308' : '#A3D4D4');
    const linked = events
      .filter((e) => e.phase_id === ph.id)
      .map((e) => ({
        id: e.id,
        title: e.title,
        description: e.description,
        due_date: e.due_date,
        progress_percent: e.progress_percent,
      }));
    const total = ph.events_total || 0;
    const done = ph.events_done || 0;
    const avg = ph.events_progress_avg;
    const pct = avg !== undefined && avg !== null
      ? Math.max(0, Math.min(100, Math.round(avg)))
      : (total > 0 ? Math.round((done / total) * 100) : (state === 'done' ? 100 : 0));
    return {
      name: ph.name,
      description: ph.description,
      start_date: ph.start_date,
      end_date: ph.end_date,
      status: state,
      accent,
      progress_percent: pct,
      events_done: done,
      events_total: total,
      kicker: ph.roadmap?.name,
      children: linked,
    };
  };

  return (
    <section data-testid="roadmap-section" className="py-14 sm:py-20 px-4 md:px-8" style={{ backgroundColor: '#F4F1EB' }}>
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <p className="text-xs uppercase tracking-[0.2em] font-bold mb-2" style={{ color: '#FF6F59', fontFamily: 'Manrope' }}>
            <Milestone className="w-4 h-4 inline mr-1.5 -mt-0.5" /> Road to Reopening
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#1A2E2E' }}>
            Our Journey Back
          </h2>
          <p className="mt-3 text-sm" style={{ color: '#4A5D5D', fontFamily: 'Manrope' }}>
            {doneCount} milestones complete · {activeCount} in progress · {phases.length - doneCount - activeCount} upcoming
          </p>
          <p className="mt-1 text-[11px] italic" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
            Tap any milestone to view the full description and linked tasks.
          </p>
        </div>

        {/* Vertical timeline */}
        <div className="relative">
          <div className="absolute left-6 sm:left-1/2 top-0 bottom-0 w-px sm:-translate-x-1/2" style={{ backgroundColor: '#A3D4D4' }} />
          <div className="space-y-8">
            {phases.map((ph, idx) => {
              const state = phaseState(ph, now);
              const Icon = state === 'done' ? CheckCircle2 : state === 'upcoming' ? Hourglass : Circle;
              const colors = state === 'done'
                ? { bg: '#22C55E', label: 'COMPLETED' }
                : state === 'active'
                  ? { bg: ph.color || '#EAB308', label: 'IN PROGRESS' }
                  : { bg: ph.color || '#A3D4D4', label: 'UPCOMING' };
              const leftSide = idx % 2 === 0;
              const total = ph.events_total || 0;
              const done = ph.events_done || 0;
              const avg = ph.events_progress_avg;
              const pct = avg !== undefined && avg !== null
                ? Math.max(0, Math.min(100, Math.round(avg)))
                : (total > 0 ? Math.round((done / total) * 100) : (state === 'done' ? 100 : 0));

              const openDialog = () => setSelected(buildMilestone(ph));

              return (
                <div key={ph.id} className="relative flex items-start sm:items-center" data-testid={`milestone-${ph.id}`}>
                  {/* Dot */}
                  <div className="absolute left-6 sm:left-1/2 sm:-translate-x-1/2 w-5 h-5 rounded-full flex items-center justify-center ring-4 ring-[#F4F1EB]" style={{ backgroundColor: colors.bg, top: '12px' }}>
                    <Icon className="w-3 h-3 text-white" />
                  </div>

                  <div className={`ml-14 sm:ml-0 sm:w-1/2 ${leftSide ? 'sm:pr-10 sm:text-right' : 'sm:pl-10 sm:ml-auto'}`}>
                    <button
                      type="button"
                      onClick={openDialog}
                      data-testid={`milestone-card-${ph.id}`}
                      className="inline-block max-w-full text-left p-4 rounded-2xl cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-lg focus:outline-none focus:ring-2"
                      style={{ backgroundColor: 'white', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}
                    >
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-[9px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: `${colors.bg}20`, color: colors.bg, fontFamily: 'Manrope' }}>{colors.label}</span>
                        {ph.description && (
                          <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: colors.bg, fontFamily: 'Manrope' }}>
                            {ph.description}
                          </span>
                        )}
                      </div>
                      <h3 className="text-xl mb-1" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#1A2E2E' }}>{ph.name}</h3>
                      <p className="text-[11px] font-bold" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
                        {fmt(parseDate(ph.start_date))} → {fmt(parseDate(ph.end_date))}
                      </p>
                      {total > 0 && (
                        <div className="mt-3">
                          <div className="h-1.5 rounded-full" style={{ backgroundColor: '#EAE5D9' }}>
                            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: colors.bg }} />
                          </div>
                          <p className="text-[10px] mt-1" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
                            {done}/{total} tasks · {pct}% complete
                          </p>
                        </div>
                      )}
                      <p className="text-[10px] mt-2 italic" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
                        View full description →
                      </p>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <MilestoneDetailDialog
        open={!!selected}
        onClose={() => setSelected(null)}
        milestone={selected}
      />
    </section>
  );
}
